"use client";

import React, { useState } from "react";
import { Facebook, Instagram, Linkedin, Menu, Quote, Star, X, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
const testimonialsData = [{
  id: 1,
  name: "Robert Harrison",
  role: "Durham Homeowner",
  content: "Five Star Roofline provided a flawless uPVC replacement. The gold-standard service and attention to detail transformed our property's appearance completely.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585014/img/avatar_maria.png",
  rating: 5
}, {
  id: 3,
  name: "David Miller",
  role: "Sunderland Client",
  content: "Our new EPDM rubber roofing is perfect. The team was punctual, tidy, and the finish is impeccable. Truly the best roofing specialists in the region.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585014/img/avatar_sofia.png",
  rating: 5
}, {
  id: 4,
  name: "Emma Thompson",
  role: "Gateshead Homeowner",
  content: "From the initial quote to the final inspection, Five Star Roofline exceeded expectations. Their black and gold brand truly reflects their elite service.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585014/img/avatar_carlos.png",
  rating: 5
}];
const socialLinks = [{
  id: "instagram",
  icon: Instagram,
  url: "#",
  label: "Instagram"
}, {
  id: "facebook",
  icon: Facebook,
  url: "#",
  label: "Facebook"
}, {
  id: "linkedin",
  icon: Linkedin,
  url: "#",
  label: "LinkedIn"
}];
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="149876" className="hidden md:flex gap-4">
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  return <div className="flex flex-col gap-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-ring transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-4 w-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-primary-foreground font-bold h-12 rounded-lg" onClick={onClose}>
        <Link to="#">GET YOUR FIVE STAR QUOTE</Link>
      </Button>
      <Separator className="my-4" />
      <div className="flex gap-4">
        {socialLinks.map((social, i) => {
        const Icon = social.icon;
        return <Link key={social.id} to={social.url} className="text-muted-foreground hover:text-ring transition-colors" aria-label={social.label} data-index={i}>
              <Icon className="size-6" strokeWidth={1.5} />
            </Link>;
      })}
      </div>
    </div>;
}
function QuoteRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-xl border border-ring/30 bg-background/50 p-8 text-center backdrop-blur-sm shadow-[0_0_20px_rgba(234,179,8,0.1)]">
        <div className="flex size-12 items-center justify-center rounded-full bg-ring/20 text-ring">
          <Star className="size-6 fill-current" />
        </div>
        <h3 className="text-2xl font-serif text-foreground">Quote Request Received!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-ring/50 text-ring hover:bg-ring/10">
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form id="get-quote-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-input/30 border-border/40 focus-visible:ring-ring focus-visible:border-ring/50" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-input/30 border-border/40 focus-visible:ring-ring focus-visible:border-ring/50" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">We'll use this to contact you.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-foreground/90 font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-14567" {...field} className="bg-input/30 border-border/40 focus-visible:ring-ring focus-visible:border-ring/50" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Include country code if outside the US.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Project Details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the roofing work you need..." className="min-h-[120px] bg-input/30 border-border/40 focus-visible:ring-ring focus-visible:border-ring/50" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">The more details, the better we can estimate.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-primary-foreground hover:brightness-110 shadow-lg shadow-amber-500/20 h-14 text-lg font-bold tracking-wide transition-all duration-300 rounded-lg">
        {isSubmitting ? <div className="flex items-center gap-2">
            <Loader className="size-5 animate-spin" />
            <span>Processing...</span>
          </div> : "GET YOUR FIVE STAR QUOTE"}
      </Button>
    </Form>;
}
export default function Testimonials() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return <section id="quoteform" className="relative flex flex-col w-full bg-background text-foreground overflow-hidden scroll-smooth">
      
      <MenuProvider menu_id="43">
        <header className="sticky top-0 z-50 w-full border-b border-border/20 bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
          <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
            <Link to="#" className="flex items-center gap-2 z-50">
              <span className="font-serif text-2xl font-bold tracking-tight text-foreground">
                Five Star <span className="text-ring">Roofline</span>
              </span>
            </Link>

            <DesktopNav />

            <div className="flex items-center gap-4">
              <Button asChild className="hidden lg:flex bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-primary-foreground hover:brightness-110 shadow-lg shadow-amber-500/20 font-bold px-6 h-10 rounded-full transition-all duration-300">
                <Link to="#">GET YOUR FIVE STAR QUOTE</Link>
              </Button>
              <div className="hidden md:flex items-center gap-3 mr-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.id} to={social.url} className="text-muted-foreground hover:text-ring transition-colors" aria-label={social.label} data-index={i}>
                      <Icon className="size-5" strokeWidth={1.5} />
                    </Link>;
              })}
              </div>
              
              <Button variant="ghost" size="icon" className="md:hidden z-50 text-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu">
                {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
              </Button>
            </div>
          </div>
        </header>

        {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-md pt-20 md:hidden">
            <MobileNav onClose={() => setIsMobileMenuOpen(false)} />
          </div>}
      </MenuProvider>

      

      <div className="dark bg-background text-foreground py-24 lg:py-32 border-t border-border/5 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-ring/5 rounded-full blur-[120px] -z-10 translate-x-1/3 translate-y-1/3" />
        
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-start max-w-6xl mx-auto">
            
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-4">
                <h2 className="text-sm font-semibold tracking-widest text-ring uppercase">complete for free qoute&nbsp;</h2>
                <h3 className="text-3xl md:text-4xl font-serif font-bold text-foreground leading-tight">
                  Get a Quote
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Tell us what work you need and we’ll get back to you quickly with a free, no-obligation quote. We cover all aspects of roofing, including repairs, fascia, soffits, guttering, and full roof replacements.
                </p>
              </div>
              
              <div className="space-y-6 pt-6 border-t border-border/10">
                <div className="flex items-center gap-4 group">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-ring/10 text-ring group-hover:bg-ring group-hover:text-black transition-colors duration-300">
                    <Star className="size-5 fill-current" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Five Star Warranty</h4>
                    <p className="text-sm text-muted-foreground">Certified materials and expert workmanship.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-ring/10 text-ring group-hover:bg-ring group-hover:text-black transition-colors duration-300">
                    <Star className="size-5 fill-current" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Local North East Team</h4>
                    <p className="text-sm text-muted-foreground">Qualified professionals based in your region.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-ring/10 text-ring group-hover:bg-ring group-hover:text-black transition-colors duration-300">
                    <Star className="size-5 fill-current" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">free no obligation quotes</h4>
                    <p className="text-sm text-muted-foreground">Get your quote instantly with no commitment.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-ring/10 text-ring group-hover:bg-ring group-hover:text-black transition-colors duration-300">
                    <Star className="size-5 fill-current" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">fast response (same day)</h4>
                    <p className="text-sm text-muted-foreground">We respond the same day to your inquiry.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-ring/10 text-ring group-hover:bg-ring group-hover:text-black transition-colors duration-300">
                    <Star className="size-5 fill-current" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">local and trusted</h4>
                    <p className="text-sm text-muted-foreground">Trusted local experts serving your area.</p>
                  </div>
                </div>
              </div>
            </div>

            <div id="get-quote-form" className="lg:col-span-3 bg-card/60 backdrop-blur-md border border-ring/20 rounded-xl p-6 md:p-10 shadow-2xl shadow-black/40 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-ring/40 to-transparent" />
              <FormProvider formKey="get_quote_form_home" formId="get_quote_form_home" sectionName="quote-form-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="GET YOUR FIVE STAR QUOTE" successMessage="Thank you for choosing Five Star Roofline! Your quote request for your North East property has been received. One of our specialists will be in touch shortly to discuss your project." errorMessage="We encountered an issue submitting your request. Please ensure all fields are correct or contact us directly for your Five Star quote.">
                <QuoteRequestForm />
              </FormProvider>
            </div>

            <div className="lg:col-span-5 flex justify-center mt-8">
              <Link to="#get-quote-form" className="text-ring hover:text-amber-400 transition-colors font-medium underline underline-offset-4">
                Back to Quote Form
              </Link>
            </div>

          </div>
        </div>
      </div>

    </section>;
}